import { RecordManagmentPopup } from '@aibees/elephant/components';
import { SearchTotal } from '@aibees/elephant/components/search-total';
import { useDataViewChartView } from '@aibees/elephant/context/data-view-context';
import {
  useSearchSimilarityFromRoute,
  useSearchTypeFromRoute,
} from '@aibees/elephant/hooks';
import { SearchParamsParamsProvider } from '@aibees/elephant/context/search-params-params-provider';
import { usePopover } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { memo, Suspense, useEffect, useMemo, useState } from 'react';
import { DataTypeProvider } from '../search-data-visualization/components/data-type-toggle/data-type-context';
import { MapViewProvider } from '../search-data-visualization/context/map-view-context';
import { config } from './config';
import { useUpdateCompanySearchItems } from './hooks/use-update-company-search-items';
import OutsideClickHandler from './outside-click-handler';
import * as Styled from './styles';
import type { SearchTableCompanyProps as Props } from './types';

const SearchTableCompany = memo(({ className }: Props) => {
  const [SearchDataVisualization, setSearchDataVisualization] =
    useState<React.FC | null>(null);

  const type = useSearchTypeFromRoute();
  const isSimilarity = useSearchSimilarityFromRoute();
  const popover = usePopover();
  const { isChartView } = useDataViewChartView();

  useUpdateCompanySearchItems();

  useEffect(() => {
    if (isChartView) {
      import('@aibees/elephant/feature/search-data-visualization')
        .then((module) =>
          setSearchDataVisualization(() => module.SearchDataVisualization)
        )
        .catch((error) =>
          console.error('Error loading SearchDataVisualization:', error)
        );
    }
  }, [isChartView]);

  const extraFilters = useMemo(() => {
    if (type) return { type };
  }, [type]);

  const columnsWithoutRelevancy = useMemo(
    () => config.columns.filter((c) => c.key !== 'relevancy'),
    []
  );
  const columns = isSimilarity ? config.columns : columnsWithoutRelevancy;

  const memoizedConfig = useMemo(
    () => ({
      ...config,
      extraFilters: { ...config.extraFilters, ...extraFilters },
      columns,
    }),
    [extraFilters, columns]
  );

  const beforeEl = useMemo(
    () => <SearchTotal viewRecordState={popover} />,
    [popover]
  );

  return (
    <OutsideClickHandler>
      {!!popover.open && <RecordManagmentPopup state={popover} type="lists" />}
      <SearchParamsParamsProvider>
        <Styled.Wrapper
          className={classnames('SearchTable', className, type, {
            isChartView,
          })}
          config={memoizedConfig}
          beforeEl={beforeEl}
          // afterEl={<SearchLimitAlertDialog />}
        />
      </SearchParamsParamsProvider>
      {isChartView && SearchDataVisualization && (
        <Suspense>
          <DataTypeProvider>
            <MapViewProvider>
              <SearchDataVisualization />
            </MapViewProvider>
          </DataTypeProvider>
        </Suspense>
      )}
    </OutsideClickHandler>
  );
});

SearchTableCompany.whyDidYouRender = true;
SearchTableCompany.displayName = 'SearchTableCompany';

export default SearchTableCompany;
